import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TIER_TINTS } from "@/components/app/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, ShieldOff, ExternalLink, CheckCircle2, Wallet, KeyRound, Send, Mail, AlertTriangle, Clock, Shirt, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SendEmailDialog from "@/components/app/SendEmailDialog";
import IssueUniformDialog from "@/components/uniform/IssueUniformDialog";

const DOC_FIELDS = [
  { key: "id_proof_front_url", label: "ID Proof — Front" },
  { key: "id_proof_back_url", label: "ID Proof — Back" },
  { key: "address_proof_front_url", label: "Address Proof — Front" },
  { key: "address_proof_back_url", label: "Address Proof — Back" },
];

const STATUS_OPTIONS = [
  { key: "probation", label: "Probation" },
  { key: "temporary", label: "Temporary" },
  { key: "permanent", label: "Permanent" },
];

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 mt-0.5 break-words">{value || <span className="text-zinc-400">—</span>}</div>
    </div>
  );
}

const fmtDob = (s) => {
  if (!s) return "";
  try {
    return new Date(s + "T00:00:00").toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) {
    return s;
  }
};

function DojRow({ userId, value, canEdit, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${userId}`, { date_of_joining: draft || null });
      toast.success(draft ? "Date of Joining saved" : "Date of Joining cleared");
      onChanged?.(draft || null);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit || !editing) {
    return (
      <div className="flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2.5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Date of Joining</div>
          <div className="text-sm text-zinc-900 mt-0.5 font-mono" data-testid="doj-value">
            {value
              ? new Date(value + "T00:00:00").toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })
              : <span className="text-zinc-400">Not set</span>}
          </div>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="doj-edit">
            {value ? "Change" : "Set date"}
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2 border border-zinc-200 rounded-md px-3 py-2.5">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Date of Joining</div>
        <Input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="doj-input" />
      </div>
      <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="doj-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
      </Button>
      <Button variant="ghost" onClick={() => { setDraft(value || ""); setEditing(false); }} data-testid="doj-cancel">Cancel</Button>
    </div>
  );
}

export default function StaffProfileDialog({ userId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState("");
  const [limitType, setLimitType] = useState("amount");
  const [limitMeta, setLimitMeta] = useState(null);
  const [savingLimit, setSavingLimit] = useState(false);

  const canManageLimit = ["super_admin", "general_manager"].includes(user?.role);
  const canAdmin = ["super_admin", "general_manager", "property_admin"].includes(user?.role);
  const canResetPassword = user?.role === "super_admin";
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const canEmail = ["super_admin", "general_manager", "property_admin"].includes(user?.role);

  // Uniform issuance state
  const [uniformCategories, setUniformCategories] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const canManageUniforms = ["super_admin", "general_manager", "property_admin"].includes(user?.role);

  const loadIssuances = useCallback(async () => {
    if (!userId || !canManageUniforms) return;
    try {
      const [{ data: items }, { data: cats }] = await Promise.all([
        api.get(`/uniform-issuances?user_id=${userId}`),
        api.get("/uniform-categories"),
      ]);
      setIssuances(items || []);
      setUniformCategories(cats || []);
    } catch (_e) {
      setIssuances([]);
    }
  }, [userId, canManageUniforms]);

  useEffect(() => { if (open) loadIssuances(); }, [open, loadIssuances]);

  const deleteIssuance = async (iid) => {
    if (!window.confirm("Delete this issuance entry?")) return;
    try {
      await api.delete(`/uniform-issuances/${iid}`);
      toast.success("Removed");
      loadIssuances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const loadEmailLogs = useCallback(async () => {
    if (!userId || !canEmail) return;
    try {
      const { data: logs } = await api.get(`/email/logs?user_id=${userId}&limit=50`);
      setEmailLogs(logs || []);
    } catch (_e) {
      setEmailLogs([]);
    }
  }, [userId, canEmail]);

  useEffect(() => { if (open) loadEmailLogs(); }, [open, loadEmailLogs]);

  const load = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const [pRes, lRes, dRes, uRes] = await Promise.all([
        api.get(`/users/${userId}/profile`),
        api.get(`/users/${userId}/advance-limit`),
        canAdmin ? api.get("/departments").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        canAdmin ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setData(pRes.data);
      setLimit(String(lRes.data?.advance_limit ?? 0));
      setLimitType(lRes.data?.advance_limit_type || "amount");
      setLimitMeta(lRes.data);
      setDepartments(dRes.data || []);
      // Eligible managers: anyone except this user themself, prefer managers/supers
      const MGR_ROLES = new Set(["super_admin", "general_manager", "property_admin", "supervisor"]);
      const sorted = (uRes.data || [])
        .filter((u) => u.id !== userId && u.active !== false)
        .sort((a, b) => {
          const ra = MGR_ROLES.has(a.role) ? 0 : 1;
          const rb = MGR_ROLES.has(b.role) ? 0 : 1;
          if (ra !== rb) return ra - rb;
          return (a.name || "").localeCompare(b.name || "");
        });
      setManagers(sorted);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load profile");
    } finally {
      setBusy(false);
    }
  }, [userId, canAdmin]);

  const saveLimit = async () => {
    const n = Number(limit);
    if (Number.isNaN(n) || n < 0) { toast.error("Enter a non-negative number"); return; }
    if (limitType === "percent" && n > 100) { toast.error("Percent must be between 0 and 100"); return; }
    setSavingLimit(true);
    try {
      const { data: res } = await api.put(`/users/${userId}/advance-limit`, {
        advance_limit: n, advance_limit_type: limitType,
      });
      setLimitMeta({ ...(limitMeta || {}), ...res });
      const label = limitType === "percent" ? `${n}% of last salary` : `₹${n.toFixed(0)}`;
      toast.success(`Advance limit set to ${label}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingLimit(false);
    }
  };

  useEffect(() => { if (open && userId) load(); }, [open, userId, load]);

  const updateUserField = async (patch, successMsg) => {
    try {
      await api.put(`/users/${userId}`, patch);
      setData((d) => (d ? { ...d, user: { ...d.user, ...patch } } : d));
      toast.success(successMsg);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async () => {
    if (newPwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put(`/users/${userId}/password`, { new_password: newPwd });
      toast.success("Password reset — share the new password with the user securely.");
      setResetPwdOpen(false);
      setNewPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingPwd(false);
    }
  };

  const verify = async () => {
    try {
      await api.post(`/users/${userId}/profile/verify`);
      toast.success("Profile verified — documents are now locked.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const unverify = async () => {
    try {
      await api.post(`/users/${userId}/profile/unverify`);
      toast.success("Profile unlocked — employee can edit again.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const profile = data?.profile || {};
  const bank = profile.bank_account || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-auto ${TIER_TINTS[data?.user?.conduct_tier]?.row || ""}`}
        data-testid="staff-profile-dialog"
        data-conduct-tier={data?.user?.conduct_tier || "none"}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Profile · {data?.user?.name || "—"}
            {data?.profile_verified && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </span>
            )}
            {data && !data.profile_completed && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded">
                Incomplete
              </span>
            )}
            {data?.user?.conduct_tier && data.user.conduct_tier !== "none" && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold rounded-full border px-2 py-0.5 ${TIER_TINTS[data.user.conduct_tier]?.pill || ""}`}
                data-testid="staff-profile-conduct-pill"
              >
                {TIER_TINTS[data.user.conduct_tier]?.label}
                {(data.user.conduct?.hard > 0 || data.user.conduct?.soft > 0) &&
                  ` · ${(data.user.conduct?.hard || 0) + (data.user.conduct?.soft || 0)} warning${((data.user.conduct?.hard || 0) + (data.user.conduct?.soft || 0)) === 1 ? "" : "s"}`}
                {data.user.conduct?.appreciations > 0 && ` · ${data.user.conduct.appreciations} appreciation${data.user.conduct.appreciations === 1 ? "" : "s"}`}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {data?.user?.employee_id ? `Employee ID: ${data.user.employee_id} · ` : ""}{data?.user?.email}
          </DialogDescription>
        </DialogHeader>

        {busy && !data ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : !data ? null : (
          <div className="space-y-6">
            <section>
              <div className="label-overline mb-2">Employment</div>
              <DojRow
                userId={userId}
                value={data?.user?.date_of_joining || ""}
                canEdit={canAdmin}
                onChanged={(v) => {
                  setData((d) => d ? { ...d, user: { ...d.user, date_of_joining: v } } : d);
                  onChanged?.();
                }}
              />
              {canAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div className="border border-zinc-200 rounded-md px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Department</div>
                    <Select
                      value={data?.user?.department || "__none"}
                      onValueChange={(v) => updateUserField({ department: v === "__none" ? null : v }, "Department updated")}
                    >
                      <SelectTrigger data-testid="dept-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— Not set —</SelectItem>
                        {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="border border-zinc-200 rounded-md px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Employment status</div>
                    <Select
                      value={data?.user?.employment_status || "probation"}
                      onValueChange={(v) => updateUserField({ employment_status: v }, "Status updated")}
                    >
                      <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="border border-zinc-200 rounded-md px-3 py-2.5 sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Reporting manager</div>
                    <Select
                      value={data?.user?.reporting_manager_id || "__none"}
                      onValueChange={(v) => updateUserField(
                        { reporting_manager_id: v === "__none" ? "" : v },
                        v === "__none" ? "Reporting manager cleared" : "Reporting manager updated",
                      )}
                    >
                      <SelectTrigger data-testid="reporting-manager-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        <SelectItem value="__none">— Not set —</SelectItem>
                        {managers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}{m.employee_id ? ` (${m.employee_id})` : ""} · {(m.role || "").replace("_", " ")}
                            {m.department ? ` · ${m.department}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {canResetPassword && (
                <div className="mt-3 flex items-center justify-between border border-amber-200 bg-amber-50 rounded-md px-3 py-2.5">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-800">Admin action</div>
                    <div className="text-sm text-amber-900 mt-0.5">Reset this user's password</div>
                  </div>
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-800" onClick={() => setResetPwdOpen(true)} data-testid="reset-password-button">
                    <KeyRound className="w-4 h-4 mr-1" /> Reset password
                  </Button>
                </div>
              )}
            </section>

            <section>
              <div className="label-overline mb-2">Documents</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {DOC_FIELDS.map((f) => {
                  const v = profile[f.key];
                  return (
                    <div key={f.key} className="border border-zinc-200 rounded p-2 bg-zinc-50/50">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</div>
                      {v ? (
                        <a
                          href={`${API}/files?path=${encodeURIComponent(v)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-zinc-900 underline inline-flex items-center gap-1 mt-1"
                          data-testid={`view-${f.key}`}
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <div className="text-xs text-zinc-400 mt-1">Not uploaded</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="label-overline mb-2">Personal & emergency</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Row label="Date of birth" value={fmtDob(profile.date_of_birth)} />
                <Row label="Blood group" value={profile.blood_group} />
                <Row label="Aadhar number" value={profile.aadhar_number} />
                <Row label="PAN number" value={profile.pan_number} />
                <Row label="Permanent address" value={profile.permanent_address} />
                <Row label="Family member name" value={profile.family_member_name} />
                <Row label="Family member phone" value={profile.family_member_phone} />
                <Row label="Emergency phone" value={profile.emergency_phone} />
                {profile.uniform_sizes && Object.keys(profile.uniform_sizes).length > 0 && (
                  <Row
                    label="Uniform sizes"
                    value={Object.entries(profile.uniform_sizes).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  />
                )}
              </div>
            </section>

            <section className="border-t border-zinc-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-overline flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Salary advance limit</div>
              </div>
              {canManageLimit ? (
                <div className="space-y-2">
                  <div className="flex items-end gap-2">
                    <div className="w-40">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Type</div>
                      <Select value={limitType} onValueChange={setLimitType}>
                        <SelectTrigger data-testid="advance-limit-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="amount">Fixed amount (₹)</SelectItem>
                          <SelectItem value="percent">% of salary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                        {limitType === "percent" ? "Percentage (0–100) — based on latest payslip" : "Per-employee limit (INR) — set 0 to disable"}
                      </div>
                      <Input
                        type="number" min="0" step={limitType === "percent" ? "1" : "100"}
                        max={limitType === "percent" ? "100" : undefined}
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        data-testid="advance-limit-input"
                      />
                    </div>
                    <Button onClick={saveLimit} disabled={savingLimit} className="bg-zinc-950 hover:bg-zinc-800" data-testid="advance-limit-save">
                      {savingLimit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save limit
                    </Button>
                  </div>
                  {limitMeta && (
                    <div className="text-[11px] text-zinc-500" data-testid="advance-limit-meta">
                      Effective cap: <span className="font-mono text-zinc-900">₹{Number(limitMeta.effective_limit || 0).toFixed(0)}</span>
                      {limitType === "percent" && (
                        <> · based on latest salary {limitMeta.salary_basis ? `₹${Number(limitMeta.salary_basis).toFixed(0)} (${limitMeta.salary_period || "—"})` : "— no payslip yet"}</>
                      )}
                      {limitMeta.note && <span className="block text-amber-700 mt-0.5">{limitMeta.note}</span>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-zinc-700">Current limit: <span className="font-mono">{limitType === "percent" ? `${Number(limit || 0)}%` : `₹${Number(limit || 0).toFixed(0)}`}</span></div>
              )}
            </section>

            {canManageUniforms && uniformCategories.length > 0 && (
              <section className="border-t border-zinc-200 pt-4" data-testid="staff-uniforms-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-overline flex items-center gap-1"><Shirt className="w-3.5 h-3.5" /> Uniforms issued</div>
                  <Button size="sm" variant="outline" onClick={() => setIssueDialogOpen(true)} data-testid="staff-uniform-add">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Record issuance
                  </Button>
                </div>
                {(() => {
                  const totals = {};
                  for (const it of issuances) {
                    totals[it.category_key] = (totals[it.category_key] || 0) + (it.quantity || 0);
                  }
                  const grand = Object.values(totals).reduce((a, b) => a + b, 0);
                  return (
                    <>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {uniformCategories.map((c) => (
                          <span key={c.key} className="inline-flex items-center gap-1.5 text-xs bg-zinc-100 rounded px-2 py-1">
                            <span className="text-zinc-500">{c.label}</span>
                            <span className="font-bold tabular-nums">{totals[c.key] || 0}</span>
                          </span>
                        ))}
                        <span className="inline-flex items-center gap-1.5 text-xs bg-zinc-950 text-white rounded px-2 py-1">
                          <span className="text-zinc-300">Total sets</span>
                          <span className="font-bold tabular-nums">{grand}</span>
                        </span>
                      </div>
                      {issuances.length === 0 ? (
                        <div className="text-xs text-zinc-400 italic">No issuances recorded yet.</div>
                      ) : (
                        <div className="border border-zinc-200 rounded-md overflow-hidden max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                              <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                                <th className="py-1.5 px-2">Date</th>
                                <th className="py-1.5 px-2">Category</th>
                                <th className="py-1.5 px-2">Size</th>
                                <th className="py-1.5 px-2 text-right">Qty</th>
                                <th className="py-1.5 px-2">By</th>
                                <th className="py-1.5 px-2 text-right"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {issuances.map((it) => (
                                <tr key={it.id} className="border-b border-zinc-100 last:border-0" data-testid={`staff-issuance-row-${it.id}`}>
                                  <td className="py-1.5 px-2 font-mono text-xs">{it.issued_at}</td>
                                  <td className="py-1.5 px-2 text-xs">{it.category_label}</td>
                                  <td className="py-1.5 px-2 font-mono text-xs">{it.size || "—"}</td>
                                  <td className="py-1.5 px-2 text-right tabular-nums font-bold text-xs">{it.quantity}</td>
                                  <td className="py-1.5 px-2 text-xs text-zinc-600">{it.issued_by_name || "—"}</td>
                                  <td className="py-1.5 px-2 text-right">
                                    <button onClick={() => deleteIssuance(it.id)} className="p-1 hover:bg-red-50 hover:text-red-600 rounded" title="Delete" data-testid={`staff-issuance-delete-${it.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
                <IssueUniformDialog
                  open={issueDialogOpen}
                  onOpenChange={setIssueDialogOpen}
                  categories={uniformCategories}
                  defaultUserId={userId}
                  defaultPropertyId={(data?.user?.property_ids || [])[0] || ""}
                  onCreated={loadIssuances}
                />
              </section>
            )}

            <section className="border-t border-zinc-200 pt-4">
              <div className="label-overline mb-2">Optional</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Row label="PF number" value={profile.pf_number} />
                <Row label="Bank account no." value={bank.account_number} />
                <Row label="Bank name" value={bank.bank_name} />
                <Row label="IFSC" value={bank.ifsc} />
                <Row label="Account holder" value={bank.account_holder_name} />
              </div>
            </section>

            {canEmail && (
              <section className="border-t border-zinc-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-overline flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Emails to this employee</div>
                  <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="staff-send-email">
                    <Send className="w-3.5 h-3.5 mr-1" /> Send email
                  </Button>
                </div>
                {emailLogs.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic">No emails sent yet.</div>
                ) : (
                  <ul className="space-y-1.5 max-h-48 overflow-auto">
                    {emailLogs.map((l) => {
                      let Icon = Clock; let color = "text-amber-700 bg-amber-50 border-amber-200";
                      if (l.status === "sent") { Icon = CheckCircle2; color = "text-emerald-700 bg-emerald-50 border-emerald-200"; }
                      if (l.status === "failed") { Icon = AlertTriangle; color = "text-red-700 bg-red-50 border-red-200"; }
                      return (
                        <li key={l.id} className="border border-zinc-200 rounded px-2 py-1.5 text-xs flex items-start gap-2" data-testid={`staff-email-log-${l.id}`}>
                          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold border rounded px-1 py-0.5 ${color}`}>
                            <Icon className="w-3 h-3" /> {l.status}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{l.subject}</div>
                            <div className="text-[10px] text-zinc-500 truncate">From {l.sender_email} · {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</div>
                            {l.error && <div className="text-[10px] text-red-600 mt-0.5 truncate" title={l.error}>{l.error}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          {data && (data.profile_verified ? (
            <Button variant="outline" onClick={unverify} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="unverify-profile">
              <ShieldOff className="w-4 h-4 mr-1" /> Unlock for re-upload
            </Button>
          ) : (
            <Button onClick={verify} disabled={!data.profile_completed} className="bg-zinc-950 hover:bg-zinc-800" data-testid="verify-profile">
              <ShieldCheck className="w-4 h-4 mr-1" /> Verify profile
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
      {/* Password reset sub-dialog */}
      <Dialog open={resetPwdOpen} onOpenChange={(o) => { setResetPwdOpen(o); if (!o) setNewPwd(""); }}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for {data?.user?.name}. They'll be notified and can change it themselves later from their profile.
            </DialogDescription>
          </DialogHeader>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">New password</div>
            <Input
              type="text"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="At least 6 characters"
              minLength={6}
              autoComplete="off"
              data-testid="reset-password-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Copy this and share with the user securely — it won't be shown again.</p>
          </div>
          <DialogFooter>
            <Button onClick={resetPassword} disabled={savingPwd || newPwd.length < 6} className="bg-zinc-950 hover:bg-zinc-800" data-testid="reset-password-confirm">
              {savingPwd ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />}
              Set new password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        title={`Email ${data?.user?.name || "employee"}`}
        defaultCategory="custom"
        toUser={data?.user ? { id: data.user.id, name: data.user.name, email: data.user.email } : null}
        onSent={loadEmailLogs}
      />
    </Dialog>
  );
}
